import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import {
  colors,
  spacing,
  mediaQuery,
  typographyMixin,
  heading,
  body,
} from 'src/constants/marketing-ds'
import { AnimatedDigit } from './AnimatedDigit'
import { selectAnalyticsReady } from 'src/modules/reducers/analytics'
import { fireUIRenderEvent } from 'src/modules/analytics/v3'
import { useSelector } from 'react-redux'
import { selectStateForProtobuf } from 'src/modules/reducers'

const ANIMATION_DURATION = 800

// Utility functions
const getTimeUntil = (targetDate) => {
  const now = new Date()
  const diff = targetDate.getTime() - now.getTime()

  if (diff <= 0) {
    return {
      totalSeconds: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    }
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  return {
    totalSeconds: Math.floor(diff / 1000),
    days,
    hours,
    minutes,
    seconds,
  }
}

const formatTimeRemaining = (timeRemaining) => {
  const { days, hours, minutes, seconds } = timeRemaining

  if (days > 0) {
    return {
      time: `${days} day${days > 1 ? 's' : ''}`,
      isStartingSoon: false,
    }
  }

  if (hours > 0) {
    return {
      time: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
      isStartingSoon: hours === 0 && minutes < 5,
    }
  }

  return {
    time: `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
    isStartingSoon: minutes < 5,
  }
}

export function AnimatedCountdownTimer({ startTime, onExpire }) {
  const startTimeDate = useMemo(() => {
    const date = typeof startTime === 'string' ? new Date(startTime) : startTime
    if (isNaN(date.getTime())) {
      console.warn('AnimatedCountdownTimer: Invalid startTime provided:', startTime)
      return new Date(Date.now() + 60000)
    }
    return date
  }, [startTime])

  const [timeRemaining, setTimeRemaining] = useState(() => getTimeUntil(startTimeDate))

  const intervalRef = useRef(null)
  const hasExpiredRef = useRef(false)
  const isEventSent = useRef(false)
  const isAnalyticsReady = useSelector(selectAnalyticsReady)
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    const updateTimer = () => {
      const remaining = getTimeUntil(startTimeDate)
      setTimeRemaining(remaining)

      if (remaining.totalSeconds <= 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true
        onExpire?.()
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    }

    const initialTimeRemaining = getTimeUntil(startTimeDate)
    updateTimer()

    if (initialTimeRemaining.totalSeconds > 0) {
      intervalRef.current = setInterval(updateTimer, 1000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [startTimeDate, onExpire])

  useEffect(() => {
    if (!isEventSent.current && isAnalyticsReady) {
      fireUIRenderEvent(
        {
          component: 'countdown_timer',
        },
        stateForProtobuf
      )
      isEventSent.current = true
    }
  }, [isAnalyticsReady])

  const formattedTime = formatTimeRemaining(timeRemaining)

  const parseTimeString = useCallback((timeStr) => {
    const parts = timeStr.split(':')
    if (parts.length === 3) {
      const hours = parts[0].padStart(2, '0')
      return {
        days: null,
        hours,
        minutes: parts[1],
        seconds: parts[2],
      }
    } else if (parts.length === 2) {
      return {
        days: null,
        hours: null,
        minutes: parts[0],
        seconds: parts[1],
      }
    } else if (parts.length === 1) {
      return {
        days: timeStr,
        hours: null,
        minutes: null,
        seconds: null,
      }
    }
    return { days: null, hours: null, minutes: null, seconds: null }
  }, [])

  const renderTimeComponent = useCallback(
    (value, type, showColon = true) => {
      if (!value) return null

      const digits = value.split('')

      return (
        <>
          {digits.map((digit, digitIndex) => (
            <AnimatedDigit
              key={`${type}-${digitIndex}`}
              digit={digit}
              isStartingSoon={formattedTime.isStartingSoon}
              animationDuration={ANIMATION_DURATION}
            />
          ))}
          {showColon && <Separator $isStartingSoon={formattedTime.isStartingSoon}>:</Separator>}
        </>
      )
    },
    [formattedTime.isStartingSoon]
  )

  if (timeRemaining.totalSeconds <= 0) {
    return null
  }

  const timeData = parseTimeString(formattedTime.time)

  return (
    <Container role="timer" aria-live="polite">
      <StartingInLabel className="countdown-timer-label">Starting in</StartingInLabel>
      <TimeRemainingContainer>
        <TimeRemainingDisplay aria-label={`Time remaining: ${formattedTime.time}`}>
          {timeData.days && (
            <Separator $isStartingSoon={formattedTime.isStartingSoon}>{timeData.days}</Separator>
          )}
          {timeData.hours != null && renderTimeComponent(timeData.hours, 'hours', true)}
          {timeData.minutes != null && renderTimeComponent(timeData.minutes, 'minutes', true)}
          {timeData.seconds != null && renderTimeComponent(timeData.seconds, 'seconds', false)}
        </TimeRemainingDisplay>
      </TimeRemainingContainer>
    </Container>
  )
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: center;
  text-align: center;
  gap: ${spacing.xxxs};
  border-radius: ${spacing.xxs};
  white-space: nowrap;
  background-color: rgba(0, 0, 0, 0.15);
  padding: ${spacing.xxxs};
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  z-index: auto;
  margin-bottom: ${spacing.xs};

  ${mediaQuery.tablet} {
    position: relative;
    top: auto;
    left: auto;
    right: auto;
    width: fit-content;
    justify-content: flex-start;
    text-align: left;
    margin-left: 0;
    margin-right: 0;
    background-color: transparent;
    padding: 0;
    z-index: auto;
  }
`

const StartingInLabel = styled.span`
  margin: 0;
  color: ${colors.semantic.text.primary};
  padding: 0;
  text-align: center;
  ${typographyMixin(body.xl)}

  ${mediaQuery.tablet} {
    text-align: left;
  }
`

const TimeRemainingDisplay = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  justify-content: center;
  ${typographyMixin(heading.lg)}

  ${mediaQuery.tablet} {
    justify-content: flex-start;
  }
`

const Separator = styled.span`
  color: ${({ $isStartingSoon }) =>
    $isStartingSoon ? colors.yellow[100] : colors.semantic.text.primary};
  font-variant-numeric: lining-nums tabular-nums;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  min-height: ${spacing.lg};
`

const TimeRemainingContainer = styled.div``
